/**
 * Where 3D COCKPIT's **dashboard quad** goes: the pure geometry half, with no
 * `three` in it.
 *
 * ## The dash stops being an overlay and becomes a thing in the aeroplane
 *
 * The painted cockpit is a screen-space canvas laid over the viewport
 * (`CockpitPanel.tsx`), so it does not move when the pilot turns his head — the
 * panel is simply swapped for the glance's own picture. With the real model
 * drawn around the camera that trick has nowhere to go: the aeroplane turns and
 * the dashboard has to turn with it, or the one part of the cockpit still being
 * painted is the one part that is nailed to the screen. So the dash band becomes
 * a quad in the aircraft's own body frame, textured from the very canvas the
 * panel was already composing.
 *
 * ## Placed by registration, not by a guessed station
 *
 * The design note proposed placing it off the enhanced generator's own
 * `cockpit_ranges`. That measurement exists in `tools/blender/airframe.py` and
 * **is not served to the browser** — `data/enhanced/` ships models and a
 * manifest, not the reference profiles — so phase 1 places the quad the way this
 * app already places the other thing it has to line up with painted art: by
 * unprojecting the painted pixels themselves (`bullet-sprites.ts`'s
 * `muzzleEyeOffsets`, the painted gun breeches, and the long account in
 * `gun-muzzles.ts` of why a screen registration is the honest answer when a
 * painted panel and a 3D view do not share a projection).
 *
 * Concretely: take the dash band's four corners in the original's 320x200 frame
 * — `x 0..320`, `y dash.originY..200` — put each where `presentTransform`
 * actually presents it, read that as an NDC about the **sheared** principal
 * point (`cockpit-projection.ts` puts the camera's axis on the painted gunsight,
 * so NDC is measured from there and not from the middle of the window), and
 * unproject through the cockpit camera at `DASH_DEPTH_M`.
 *
 * What that buys is the property the feature most needs and the eye most
 * quickly checks: **switching the setting on does not move the dashboard.** The
 * forward view is pixel-for-pixel where it was, and everything that changes is
 * what is around it. What it costs is the same cost the breech registration
 * pays: the quad's *physical* size depends on the window it was registered
 * against, so a resize moves it a centimetre or two. Only a glance can see that,
 * and only as a fraction of a degree.
 *
 * ## The near plane
 *
 * `CAMERA_NEAR_M` is 1 m, which is further from the pilot's eye than any part of
 * a Sopwith Camel's cockpit. Left alone it would clip away the dash quad, the
 * coaming, the fuselage sides and the cabane struts — everything this feature
 * exists to show — and leave a cowl floating in a hole. `COCKPIT_3D_NEAR_M`
 * below is the near plane the cockpit uses instead while 3D COCKPIT is drawing,
 * and it is affordable for a reason `flight-view.ts` already wrote down: the
 * renderer runs a **reversed-Z float depth buffer**, whose precision is
 * *relative* (~1e-6 of z) rather than concentrated in the first few metres, so
 * pulling the near plane in by a factor of eight does not cost the terrain
 * layers the separation reversed-Z was turned on to give them. Nothing outside
 * the 3D cockpit sees the change.
 */

import { frameIsPainted, frameStats, type CockpitDrawOp, type FrameStats } from "@red-opus/baker/schema";
import { presentTransform, LOGICAL_HEIGHT, LOGICAL_WIDTH } from "./panel-layout.ts";
import { cockpitPrincipalPoint } from "./cockpit-projection.ts";

/**
 * How far ahead of the pilot's eye the dash quad hangs, in metres.
 *
 * **INFERRED**, and bounded rather than measured. It must be nearer than the
 * painted gun breeches — `gun-muzzles.ts`'s `breechDepthM` puts the Camel's
 * muzzle 1.585 m ahead of the eye, and an instrument panel is not out on the
 * cowl with the Vickers' business end — and it must clear the near plane below
 * with room for the pilot's own coaming to be drawn in front of it. Half a metre
 * is where an instrument panel sits in a scout whose cockpit a man can touch
 * both sides of.
 *
 * It does **not** change what the dash looks like: the corners are unprojected
 * *at* this depth, so the quad's size scales with it and its projection is the
 * same rectangle whatever it is set to. What it changes is where the quad sits
 * relative to the model — what occludes it, and how far it swings out of frame
 * as the pilot turns his head.
 */
export const DASH_DEPTH_M = 0.5;

/**
 * The near plane while 3D COCKPIT is drawing — see the module doc for why the
 * ordinary 1 m one cannot stay and why this one is cheap.
 *
 * Chosen to clear the closest thing the camera can be asked to draw: the quad at
 * `DASH_DEPTH_M`, less the coaming and fuselage sides that stand between the
 * pilot's eye and it. 0.08 m leaves the whole cockpit in front of the lens with
 * a margin, and is still 80 mm — the camera never gets inside a polygon it
 * would then see the back of at arm's length.
 */
export const COCKPIT_3D_NEAR_M = 0.08;

/** A point in the aircraft's body frame: right +X, up +Y, **forward −Z** (the camera convention `muzzleEyeOffsets` returns). */
export interface EyeFramePoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** The quad's four corners in the eye frame, in `topLeft, topRight, bottomLeft, bottomRight` order. */
export interface DashQuadCorners {
  readonly topLeft: EyeFramePoint;
  readonly topRight: EyeFramePoint;
  readonly bottomLeft: EyeFramePoint;
  readonly bottomRight: EyeFramePoint;
}

/** The viewport a dash quad is registered against — the same triple `MuzzleViewport` carries, for the same reason. */
export interface DashViewport {
  readonly widthPx: number;
  readonly heightPx: number;
  /** The **rendered** vertical FOV, i.e. `renderedFovDeg(camera)` — not `camera.fov`, which carries the shear. */
  readonly fovDeg: number;
}

/**
 * One logical 320x200 pixel, unprojected through the presented panel and the
 * sheared cockpit camera onto the plane `z = -depthM`.
 *
 * Line for line the same three steps `muzzleEyeOffsets` takes, and deliberately
 * so: if these two ever disagreed, the painted breeches and the painted dash
 * they are drawn on would come apart.
 */
function unprojectLogical(x: number, y: number, viewport: DashViewport, depthM: number): EyeFramePoint {
  const width = Math.max(viewport.widthPx, 1);
  const height = Math.max(viewport.heightPx, 1);
  const present = presentTransform(width, height);
  const tanY = Math.tan((viewport.fovDeg * Math.PI) / 360);
  const tanX = tanY * (width / height);
  const principal = cockpitPrincipalPoint(width, height);
  const canvasX = present.rect.x + x * present.scaleX;
  const canvasY = present.rect.y + y * present.scaleY;
  const ndcX = (canvasX - principal.x) / (width / 2);
  const ndcY = (principal.y - canvasY) / (height / 2);
  return { x: depthM * tanX * ndcX, y: depthM * tanY * ndcY, z: -depthM };
}

/** A rectangle of the original's 320x200 frame: `left`/`top` inclusive, `right`/`bottom` exclusive. */
export interface LogicalRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** The whole logical frame — what `dashArtBounds` falls back to when it is handed nothing to measure. */
const WHOLE_FRAME: LogicalRect = { left: 0, top: 0, right: LOGICAL_WIDTH, bottom: LOGICAL_HEIGHT };

function growToOp(box: { left: number; top: number; right: number; bottom: number }, x: number, y: number, w = 0, h = 0): void {
  box.left = Math.min(box.left, x);
  box.top = Math.min(box.top, y);
  box.right = Math.max(box.right, x + w);
  box.bottom = Math.max(box.bottom, y + h);
}

/**
 * The smallest logical rectangle the kept (dashboard-family) ops actually
 * touch, clamped to the frame.
 *
 * **This is where phase 1 deviates from the design note, which said to crop the
 * quad to the dash band, `y >= dash.originY`.** The note also — in the same
 * paragraph — lists the gun-breech and gunsight sprites as members of the
 * dashboard family, and on the Camel those are painted at logical y 71 to 95,
 * *above* the band. Cropping at 98 would have thrown away the guns and the
 * sight, i.e. the two things the note asked to keep. Measuring the kept art's
 * own extent keeps them and still gives the quad a real size rather than a
 * full-frame decal's: on `CAMEL` it comes out at y 71..200 instead of 0..200.
 *
 * A sprite's extent needs its frame's size, which is why `sheetFrames` is here;
 * a sprite whose sheet or frame is missing contributes its origin alone, which
 * can only ever make the box smaller than the art and never larger than the
 * frame.
 */
export function dashArtBounds(
  ops: readonly CockpitDrawOp[],
  sheetFrames: Readonly<Record<string, readonly { readonly width: number; readonly height: number }[]>>,
): LogicalRect {
  if (ops.length === 0) return WHOLE_FRAME;
  const box = { left: Number.POSITIVE_INFINITY, top: Number.POSITIVE_INFINITY, right: Number.NEGATIVE_INFINITY, bottom: Number.NEGATIVE_INFINITY };
  for (const op of ops) {
    if (op.kind === "poly") for (const point of op.points) growToOp(box, point.x, point.y);
    else if (op.kind === "line") {
      growToOp(box, op.x0, op.y0);
      growToOp(box, op.x1, op.y1);
    } else if (op.kind === "circle") growToOp(box, op.x - op.radiusX, op.y - op.radiusY, 2 * op.radiusX, 2 * op.radiusY);
    else if (op.kind === "rect") growToOp(box, op.x, op.y, op.width, op.height);
    else if (op.kind === "sprite") {
      const frame = sheetFrames[op.sheet]?.[op.frame];
      growToOp(box, op.x, op.y, frame?.width ?? 0, frame?.height ?? 0);
    } else growToOp(box, op.x, op.y, 1, 1);
  }
  return {
    left: Math.max(0, Math.min(box.left, LOGICAL_WIDTH)),
    top: Math.max(0, Math.min(box.top, LOGICAL_HEIGHT)),
    right: Math.min(LOGICAL_WIDTH, Math.max(box.right, 0)),
    bottom: Math.min(LOGICAL_HEIGHT, Math.max(box.bottom, 0)),
  };
}

/**
 * The quad's four corners in the eye frame, for a logical rectangle of the
 * painted frame.
 */
export function dashQuadCorners(art: LogicalRect, viewport: DashViewport, depthM: number = DASH_DEPTH_M): DashQuadCorners {
  return {
    topLeft: unprojectLogical(art.left, art.top, viewport, depthM),
    topRight: unprojectLogical(art.right, art.top, viewport, depthM),
    bottomLeft: unprojectLogical(art.left, art.bottom, viewport, depthM),
    bottomRight: unprojectLogical(art.right, art.bottom, viewport, depthM),
  };
}

/**
 * The sub-rectangle of the composed 320x200 canvas the quad is textured from,
 * as `three`'s `texture.offset`/`texture.repeat`.
 *
 * `three` measures texture V **upwards from the bottom** while a canvas measures
 * y downwards from the top — the same flip seam the glance-flip lane spent a day
 * inside — and a `CanvasTexture` arrives with `flipY` true, so canvas row 0 is
 * V = 1 and canvas row 200 is V = 0. The rectangle's **top** row is therefore
 * its **largest** V, and `offsetY` is measured from the canvas's *bottom* edge:
 * `1 - bottom/height`. For art that runs to the last row that is 0, which is
 * exactly the value a wrong derivation also produces — so the test pins a case
 * where it is not (`bottom < 200`) and checks the flipped variant fails.
 */
export interface DashUvWindow {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly repeatX: number;
  readonly repeatY: number;
}

export function dashUvWindow(art: LogicalRect): DashUvWindow {
  return {
    offsetX: art.left / LOGICAL_WIDTH,
    offsetY: 1 - art.bottom / LOGICAL_HEIGHT,
    repeatX: (art.right - art.left) / LOGICAL_WIDTH,
    repeatY: (art.bottom - art.top) / LOGICAL_HEIGHT,
  };
}

/**
 * What the quad is textured from: a **snapshot** of the composed cockpit frame,
 * never the canvas the panel is still drawing into.
 *
 * `ImageBitmap` is the whole point. `createImageBitmap` hands back an immutable,
 * detached image the compositor owns; the panel's own 320x200 canvas is a
 * surface it clears and repaints roughly twelve times a second, and pointing the
 * GPU at that made the WebGPU canvas **unreadable** — `canvas.toDataURL()`
 * returned in about a second with 3D COCKPIT off and never returned with it on,
 * reproducibly, on a loaded box and on an idle one
 * (`docs/engineering/lessons.md`). A snapshot also severs the panel↔scene
 * coupling: after the copy, neither owns anything the other is touching.
 *
 * The fallback branch keeps a canvas usable as a source, so a browser without
 * `createImageBitmap` still gets a dashboard rather than an empty quad.
 */
export type DashFrameSource = ImageBitmap | HTMLCanvasElement;

/**
 * The snapshot, its extent, and a counter — published by `CockpitPanel.tsx` and
 * consumed by `scene-cockpit-3d.ts`'s quad.
 *
 * A plain mutable object rather than a callback or a scene method, and shared by
 * a route that hands the same one to both — the shape `flight-scene.ts` already
 * uses for the ground and scenery swap refs (`{ current: GroundQuery }`). It
 * cannot get the two out of order (the panel may publish before the scene
 * exists, or never), and it needs no teardown.
 *
 * It lives in this module rather than beside the quad so neither the panel nor
 * `flight-scene-api.ts` has to reach into a module that imports `three`.
 */
/**
 * What the last published snapshot actually had *in* it, in three numbers.
 *
 * This exists because of the way 3D COCKPIT phase 1 failed. The quad reported
 * itself visible, mapped, positioned to six decimal places and freshly uploaded
 * thirty-six times — and drew nothing, because it had been taken out of the
 * scene graph by `loadAircraft` and none of those readings could tell. Every one
 * of them was about the quad's *intent*. So the probe now carries the two things
 * intent cannot fake: whether the mesh is in the scene at all
 * (`Cockpit3dQuad.report`), and whether the picture it is textured from has any
 * paint in it — which is this.
 *
 * The arithmetic itself is `@red-opus/baker`'s `frameStats`, so the bake's own
 * test can make the same measurement against the real `.TTM` and `.BMP` files
 * (`tools/bake/test/cockpit-airframe.test.ts`) rather than a second copy of it.
 */
export type DashFrameStats = FrameStats;
export const dashFrameStats = frameStats;
export const dashFrameIsPainted = frameIsPainted;

export interface DashCanvasFeed {
  source: DashFrameSource | undefined;
  /** The logical rectangle of the frame that actually carries paint (`dashArtBounds`). */
  art: LogicalRect | undefined;
  /** What was in the last published frame, for the probe — `undefined` until one has been measured. */
  stats: DashFrameStats | undefined;
  /**
   * Bumped on every published snapshot. This is what makes it a feed rather than
   * a poll: a texture has to be told its source changed, the panel is the only
   * thing that knows when that is, and it repaints about twelve times a second
   * against a display rate of sixty.
   */
  version: number;
}

export function createDashCanvasFeed(): DashCanvasFeed {
  return { source: undefined, art: undefined, stats: undefined, version: 0 };
}

/**
 * Publishes one snapshot. The scene closes the bitmap it replaces once it has
 * uploaded the new one, so this deliberately does *not* close anything: the
 * frame it is dropping a reference to may be the one the GPU is reading.
 */
export function publishDashFrame(feed: DashCanvasFeed, source: DashFrameSource, art: LogicalRect, stats?: DashFrameStats): void {
  feed.source = source;
  feed.art = art;
  // Kept when this publish did not measure — the probe wants the last reading
  // there was, not a hole every time the throttle skipped a frame.
  if (stats) feed.stats = stats;
  feed.version++;
}

/**
 * One snapshot request, with the "only one in flight" rule the panel needs.
 *
 * `createImageBitmap` is asynchronous and the panel repaints on a timer, so
 * without a gate a slow encode would queue snapshots behind each other and the
 * quad would show an ever-later picture. Dropping a repaint is free — the next
 * one is 80 ms away and carries the same instruments.
 *
 * Returns `false` when the request was skipped, which is what a test asserts
 * instead of reaching for a real `createImageBitmap`.
 */
export interface DashSnapshotGate {
  busy: boolean;
}

export function createDashSnapshotGate(): DashSnapshotGate {
  return { busy: false };
}

/**
 * Snapshots `frame` into the feed, unless one is already being taken.
 *
 * The synchronous fallback matters as much as the fast path: a browser with no
 * `createImageBitmap` publishes the canvas itself and gets the pre-snapshot
 * behaviour, which drew correctly — it was only the readback that suffered.
 */
export function snapshotDashFrame(
  feed: DashCanvasFeed,
  gate: DashSnapshotGate,
  frame: HTMLCanvasElement,
  art: LogicalRect,
  createBitmap: ((source: HTMLCanvasElement) => Promise<ImageBitmap>) | undefined,
  stats?: DashFrameStats,
): boolean {
  if (!createBitmap) {
    publishDashFrame(feed, frame, art, stats);
    return true;
  }
  if (gate.busy) return false;
  gate.busy = true;
  createBitmap(frame)
    .then((bitmap) => publishDashFrame(feed, bitmap, art, stats))
    .catch(() => {
      // A refused snapshot costs this repaint and nothing else; the quad keeps
      // showing the last one it got.
    })
    .finally(() => {
      gate.busy = false;
    });
  return true;
}
